import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import FilterList from './oc-filter-list';
import ResultList from './oc-result-list';
import { NAMES, NESTED_OPTIONS } from './second-tier-filters';
import { connectURLSearchParams } from '../../../redux/connectors/URLSearchParamConnector';
import { isOptionSelected } from '../../helpers/oc-filter-list-helper';
import { connectLocation } from '../../location/LocationConnectors';
import FilterableListContainer, { TABS } from '../layout/FilterableList/FilterableListContainer';
import TabContentContainer from '../layout/FilterableList/TabContentContainer';

const defaultPageSize = 20;

// TODO assumption is filter option has "value", maybe we allow a getter there too

const wrapValueInArray = value => (Array.isArray(value) ? value : [value]);

const defaultGetter = (item, attribute) => _.get(item, attribute);

const getValueFromDataset = (dataset, propertyPath) => {
  if (typeof dataset !== 'object') {
    return null; // or some default value
  }

  if (!propertyPath) {
    // If propertyPath is not provided, return the entire dataset
    return dataset;
  }

  const pathArray = Array.isArray(propertyPath) ? propertyPath : [propertyPath];

  let currentValue = dataset;

  for (const propertyName of pathArray) {
    if (!currentValue || typeof currentValue !== 'object') {
      return dataset;
    }

    currentValue = currentValue[propertyName];

    if (currentValue === undefined) {
      return null;
    }
  }

  return currentValue;
};

// Compares as strings so a selected filter value matches an item value regardless of type:
// the URL param decoder can yield numbers (e.g. 32) while item/option values are strings ('32').
const includesAsString = (values, target) => wrapValueInArray(values).map(String).includes(String(target));

export const defaultMatchersByFilterThenValueTypes = {
  multiple: {
    multiple: (item, filterValue, attribute, itemValue) =>
      wrapValueInArray(filterValue).some(filter => includesAsString(wrapValueInArray(itemValue), filter)),
    singular: (item, filterValue, attribute, itemValue) => includesAsString(wrapValueInArray(filterValue), itemValue),
    none: () => true,
  },
  singular: {
    multiple: (item, filterValue, attribute, itemValue) => includesAsString(itemValue || [], filterValue),
    singular: (item, filterValue, attribute, itemValue) => String(itemValue) === String(filterValue),
    none: () => true,
    search: (item, filterValue, attribute, itemValue, filter) => {
      const searchValue = filterValue?.trim()?.toLowerCase();
      return !searchValue
        ? true
        : filter.fields?.some(field => {
            const fieldValue = getValueFromDataset(item, field);
            if (!fieldValue) return false;
            return Array.isArray(fieldValue)
              ? fieldValue.some(n => _.toString(n).toLowerCase().includes(searchValue))
              : _.toString(fieldValue).toLowerCase().includes(searchValue);
          });
    },
  },
};

// hierarchical options nest, so the vocabulary is the whole tree
const optionValues = (options = []) => options.flatMap(option => [option.value, ...optionValues(option.children)]);

// drop values with no matching option - they can't be labelled in a chip or cleared from
// the pane, so applying them strands the user. no options means free-form (search, distance).
const selectedOptionValues = (filter, value) => {
  if (_.isEmpty(filter.options)) {
    return value;
  }

  const allowed = optionValues(filter.options);

  if (Array.isArray(value)) {
    const known = value.filter(entry => includesAsString(allowed, entry));
    return _.isEmpty(known) ? undefined : known;
  }

  return includesAsString(allowed, value) ? value : undefined;
};

const getGetter = filter => filter.getter || defaultGetter;

const compactUrlParams = params => {
  Object.keys(params || {}).forEach(key => {
    if (_.isPlainObject(params[key])) {
      compactUrlParams(params[key]);
    }

    if (!_.isInteger(params[key]) && _.isEmpty(params[key])) {
      delete params[key];
    }
  });

  return params;
};

const removeFilterAttribute = (params, attribute) => {
  Object.keys(params || {}).forEach(key => {
    if (key === attribute) {
      delete params[key];
      return;
    }

    if (_.isPlainObject(params[key])) {
      removeFilterAttribute(params[key], attribute);
    }
  });

  return compactUrlParams(params);
};

const removeFilterOption = (params, attribute, option) => {
  Object.keys(params || {}).forEach(key => {
    if (key === attribute) {
      const remaining = wrapValueInArray(params[key]).filter(value => String(value) !== String(option));

      if (_.isEmpty(remaining)) {
        delete params[key];
      } else {
        params[key] = Array.isArray(params[key]) ? remaining : remaining[0];
      }

      return;
    }

    if (_.isPlainObject(params[key])) {
      removeFilterOption(params[key], attribute, option);
    }
  });

  return compactUrlParams(params);
};

const getMatcher = filter => {
  const valueType = filter.valueType || 'none';
  const filterType = filter.component.filterValueType;
  const response = filter.matcher || defaultMatchersByFilterThenValueTypes[filterType]?.[valueType];
  if (!response)
    throw new Error(
      `Cannot identify filter function for ${filter.name}.  Add \`filterValueType\` with value "multiple" or "singular" to your component or supply a \`matcher\` function on the filter definition.`
    );
  return response;
};

const FilterListResults = connectURLSearchParams(
  connectLocation(
    ({
      t,
      allowSearch,
      contentHeader,
      filters,
      getFilterTargets,
      lists,
      loading,
      noResultComponent,
      primaryActiveDefault = true,
      resultComponent,
      resultComponentStyle,
      resultGridSize,
      resultHeaderComponent,
      secondaryContent,
      pagination,
      countFromApi,
      appliedFilterLabel,
      tabs = null,
      filterChipsVisible = TABS.SECONDARY,
      sorts,
      updatesPathFromParams = true,
      serverSideCalculation = false,
      serverSideCalculationUpdate,
      userLocation = null,
      urlParams = {},
      setUrlParams,
      locale,
      excludeParams = [],
      showLogo = false,
      filterScope = 'oc',
      withSecondTier = false,
    }) => {
      appliedFilterLabel ||= `${t('filter_list.applied_filters')}:`;
      const defaultActiveTab = urlParams[filterScope]?.[TABS.TAB_PARAM]; // required for tabs component
      const primaryActive = defaultActiveTab ? defaultActiveTab === TABS.PRIMARY : primaryActiveDefault;

      const updatePathFromParams = nextUrlParams => {
        const updatedUrlParams = compactUrlParams(_.cloneDeep(nextUrlParams || {}));

        setUrlParams(updatedUrlParams);

        if (serverSideCalculation) {
          serverSideCalculationUpdate(updatedUrlParams);
        }
      };

      const updateParams = (attribute, option) => {
        let params = _.cloneDeep(urlParams);
        params[filterScope] ||= {};

        if (attribute === 'tab' && !filters[attribute]) {
          // if attribute is meant for tabs only and not a filter type
          handleTabUpdate(params, option);
          return;
        }

        // In case filter has 'unscoped' set to true, pass the option directly to the params
        if (filters[attribute]?.unscoped) {
          params[attribute] = option;
          updatePathFromParams(params);
          return;
        }

        if (filters[attribute]?.component?.filterValueType === 'multiple') {
          params = updateArrayParams(params, attribute, option);
        } else {
          params = updateStringParams(params, attribute, option);
        }

        if (attribute !== 'page' && params[filterScope]?.page && params[filterScope]?.page !== '1') {
          params[filterScope].page = '1';
        }

        updatePathFromParams(params);
      };

      const resetAllFilters = () => {
        const params = _.cloneDeep(urlParams);

        Object.keys(filters || {})
          .filter(attribute => !excludeParams.includes(attribute))
          .forEach(attribute => removeFilterAttribute(params, attribute));

        updatePathFromParams(params);
      };

      const resetFilter = attribute => {
        const params = removeFilterAttribute(_.cloneDeep(urlParams), attribute);
        updatePathFromParams(params);
      };

      const updateArrayParams = (params, attribute, option) => {
        const nextParams = _.cloneDeep(params);
        const scopedParams = nextParams[filterScope] || {};
        nextParams[filterScope] = scopedParams;

        scopedParams[attribute] = Array.wrap(scopedParams[attribute]);
        const hasOption = isOptionSelected(option, scopedParams[attribute]);
        const hasAppliedOption = isOptionSelected(option, appliedFilters[attribute]);

        if (hasOption || hasAppliedOption) {
          removeFilterOption(nextParams, attribute, option);
        } else {
          scopedParams[attribute] ||= [];
          scopedParams[attribute].push(option);
        }
        return nextParams;
      };

      const updateStringParams = (params, attribute, option) => {
        const scopedParams = _.cloneDeep(params[filterScope] || {}); // Start with the scoped params
        if (
          String(scopedParams[attribute]) === String(option) ||
          String(appliedFilters[attribute]) === String(option)
        ) {
          return removeFilterOption(params, attribute, option);
        }

        // Update the attribute with the new value
        scopedParams[attribute] = option;

        if (filters[attribute]?.onChange) {
          filters[attribute].onChange(option);
        }

        params[filterScope] = scopedParams;
        return params;
      };

      const handleTabUpdate = (params, tabOption) => {
        params[filterScope].tab = tabOption;
        updatePathFromParams(params);
      };

      const matchByMatchType = (item, attribute, filterValue) => {
        const filter = filters[attribute];

        if (!filter) {
          return true;
        }

        const getter = getGetter(filter);
        const matcher = getMatcher(filter);

        return matcher(item, filterValue, attribute, getter(item, attribute), filter);
      };

      const matchesFilters = (target, filterValues) =>
        Object.keys(filterValues).every(attribute => matchByMatchType(target, attribute, filterValues[attribute]));

      // An item may present several targets to the filters (e.g. one per variant of it),
      // and it matches when a single one of them meets every applied filter.
      const applyFilters = (list, filterValues) => {
        if (serverSideCalculation) return list || [];

        return (list || []).filter(item =>
          (getFilterTargets ? getFilterTargets(item) : [item]).some(target => matchesFilters(target, filterValues))
        );
      };

      const getFilteredList = () => {
        let listNumber = 1;

        return lists.map(list => {
          const resultObject = { name: list.name, headerData: list.headerData };

          resultObject.data = applyFilters(list.data, appliedFilters);
          resultObject.data = sortList(resultObject.data);

          resultObject.data.map(item => {
            item.list_number = listNumber++;
          });

          return resultObject;
        });
      };

      const formatMapData = list => {
        if (!list) return [];
        return list.flatMap(group => group.data);
      };

      const getValidFilters = allFilters =>
        Object.keys(allFilters).reduce((uniqueFilters, key) => {
          if (key === filterScope) {
            return {
              ...uniqueFilters,
              ...getValidFilters(allFilters[key]),
            };
          }
          if (filters?.[key] !== undefined) {
            const selected = selectedOptionValues(filters[key], allFilters[key]);

            return selected === undefined
              ? uniqueFilters
              : {
                  ...uniqueFilters,
                  [key]: selected,
                };
          }
          return uniqueFilters;
        }, {});

      const getAppliedFilters = () => {
        const scopedFilters = urlParams || {};
        // Ensure we only include valid filters from the configuration
        return getValidFilters(scopedFilters);
      };

      const getResultsCount = () => {
        const reducer = (count, resultGroup) => count + resultGroup.data.length;
        return filteredList.reduce(reducer, 0);
      };

      const getAppliedSort = () => {
        // Provision for default sort attribute. Add 'default: true' to sort field
        const defaultSort =
          sorts && sorts.fields ? Object.keys(sorts.fields).filter(field => sorts.fields[field].default)?.[0] : null;
        return urlParams[filterScope]?.sort || defaultSort || '';
      };

      const getSearchValue = () => {
        const searchValue = _.get(urlParams, `${filterScope}.q`, '');
        return _.toString(searchValue).trim();
      };

      const sortList = listToSort => {
        const appliedSort = getAppliedSort();
        if (!appliedSort || appliedSort.length === 0) return listToSort;
        const sortProperties = sorts.fields[appliedSort];
        if (!sortProperties) return listToSort;
        const sortAttribute = sortProperties.attribute;
        if (!sortAttribute) return listToSort; // Reset sort by passing null value against attribute
        if (sortProperties.direction === 'desc') {
          return listToSort.sort((firstEl, secondEl) => (firstEl[sortAttribute] < secondEl[sortAttribute] ? 1 : -1));
        }
        return listToSort.sort((firstEl, secondEl) => (firstEl[sortAttribute] > secondEl[sortAttribute] ? 1 : -1));
      };

      const appliedFilters = getAppliedFilters();
      const filteredList = getFilteredList();
      const totalCount = countFromApi || getResultsCount();

      if (pagination) {
        if (typeof pagination === 'boolean') {
          pagination = {};
        }
        pagination = {
          page: urlParams[filterScope]?.page || 1,
          perPage: defaultPageSize,
          ...pagination,
        };
        const totalPages = Math.ceil((1.0 * totalCount) / pagination.perPage);
        pagination = {
          updateParams,
          ...pagination,
          totalPages,
        };
      }

      const getCount = (list, attribute, filterValue) => {
        const otherFilters = _.omit(appliedFilters, attribute);
        otherFilters[attribute] = filterValue;

        return applyFilters(list, otherFilters).length;
      };

      const getCounts = (attribute, target, options) => {
        options.forEach(option => {
          target[option.value] = lists.reduce((prev, curr) => prev + getCount(curr.data, attribute, option.value), 0);
          if (option.children) {
            getCounts(attribute, target, option.children);
          }
        });
      };

      const filterCounts = useMemo(() => {
        const counts = {};

        Object.keys(filters || {}).forEach(attribute => {
          if (!filters[attribute].suppressCount) {
            if (serverSideCalculation) {
              counts[attribute] = filters[attribute].filterCounts;
            } else {
              counts[attribute] = {};
              getCounts(attribute, counts[attribute], filters[attribute].options);
            }
          }
        });

        return counts;
      }, [lists, filters, appliedFilters]);

      const mapData = formatMapData(filteredList);
      const contentHeaderProps = Object.assign(contentHeader?.props || {}, {
        primaryActive,
        secondaryActive: !primaryActive,
        count: totalCount,
        updateParams,
        urlParams,
        searchValue: contentHeader?.props?.searchValue || getSearchValue(),
        filterScope,
      });
      const sortProps = sorts?.component
        ? {
            ...sorts.props,
            options: Object.keys(sorts.fields).map(value => ({
              value,
              displayName: sorts.fields[value].displayName,
            })),
            updateParams,
            selected: getAppliedSort(),
          }
        : {};

      const ContainerComponent = tabs ? TabContentContainer : FilterableListContainer;

      if (withSecondTier && filters && _.some(Object.keys(NESTED_OPTIONS), key => filters[key])) {
        NAMES.forEach(key => {
          if (filters[key]) {
            // eslint-disable-next-line no-param-reassign
            filters[key].secondTier = true;
          }
        });
      }

      return (
        <ContainerComponent
          allowSearch={allowSearch}
          appliedFilters={appliedFilters}
          appliedFilterLabel={appliedFilterLabel}
          filterContent={
            filters ? (
              <FilterList
                filters={filters}
                appliedFilters={appliedFilters}
                filterCounts={filterCounts}
                updateParams={updateParams}
                resetFilter={resetFilter}
                loading={loading}
              />
            ) : undefined
          }
          filterHeading={t('filter_list.filters')}
          filterReset={resetAllFilters}
          filters={filters}
          headerContent={{ component: contentHeader?.component, props: contentHeaderProps }}
          loading={loading}
          noResultContent={noResultComponent}
          primaryActiveDefault={primaryActive}
          primaryContent={
            <ResultList
              pagination={pagination}
              resultHeaderComponent={resultHeaderComponent}
              results={filteredList}
              resultComponent={resultComponent}
              resultComponentStyle={resultComponentStyle}
              resultGridSize={resultGridSize}
              appliedFilters={appliedFilters}
              locale={locale}
              showLogo={showLogo}
            />
          }
          resultCount={contentHeaderProps.count}
          secondaryContent={secondaryContent ? secondaryContent({ locations: mapData, userLocation }) : null}
          resultHeaderComponent={resultHeaderComponent}
          results={filteredList}
          tabs={tabs}
          defaultActiveTab={defaultActiveTab}
          filterChipsVisible={filterChipsVisible}
          sortContent={
            sorts?.component ? { component: sorts.component, props: sortProps, fields: sorts.fields } : undefined
          }
        />
      );
    }
  )
);

FilterListResults.propTypes = {
  filters: PropTypes.object,
  getFilterTargets: PropTypes.func,
  lists: PropTypes.array,
  resultComponent: PropTypes.elementType,
  resultHeaderComponent: PropTypes.elementType,
  sorts: PropTypes.object,
  secondaryContent: PropTypes.elementType,
  contentHeader: PropTypes.object,
  countFromApi: PropTypes.number,
};

export default props => <FilterListResults {...props} />;
